/**
 * components/ui/ClientAvatar.tsx
 *
 * Shared avatar component for clients.
 * Shape is the key visual differentiator between client types:
 *   - Individual → rounded-full
 *   - Company    → rounded-xl (soft square)
 * Both types use the same colour palette; initials are always shown.
 *
 * Accepts two usage patterns:
 *   1. Preferred: pass `client` (full record) for best initials + shape.
 *   2. Legacy:    pass `displayName` + optional `clientType` (computed by caller).
 *
 * Updated Apr 2026: unified colour + shape system; added 'xs'/'lg' sizes;
 *   replaced Building2 icon with coloured initials for company avatars;
 *   made `client` the preferred prop, `displayName`/`clientType` kept for compat.
 */

import { getClientInitials, isCompany } from '../../utils/clientDisplay';
import type { Database } from '../../types/database';
import { getInitials } from '../../utils/stringHelpers';

type ClientLike = Pick<
  Database['public']['Tables']['clients']['Row'],
  'client_type' | 'first_name' | 'last_name' | 'name' | 'email' | 'company_name' | 'company_registration_number'
>;

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-emerald-500',
  'bg-sky-500',
];

function getAvatarColor(seed: string): string {
  const hash = seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const SIZE_CLASSES = {
  xs:  { container: 'w-7 h-7',   text: 'text-[10px]' },
  sm:  { container: 'w-8 h-8',   text: 'text-xs'     },
  md:  { container: 'w-10 h-10', text: 'text-sm'      },
  lg:  { container: 'w-14 h-14', text: 'text-xl'      },
};

type ClientAvatarProps = {
  hasUnread?: boolean | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
} & (
  | { client: ClientLike; displayName?: never; clientType?: never }
  | { client?: never; displayName: string | null; clientType?: 'individual' | 'company' | null }
);

export default function ClientAvatar({
  client,
  displayName: displayNameProp,
  clientType,
  hasUnread,
  size = 'sm',
}: ClientAvatarProps) {
  const company = client ? isCompany(client) : clientType === 'company';
  const shapeClass = company ? 'rounded-xl' : 'rounded-full';
  const { container, text } = SIZE_CLASSES[size];

  const initials = client
    ? getClientInitials(client)
    : getInitials(displayNameProp || '?');

  const colorSeed = client
    ? (client.company_name || [client.last_name, client.first_name].filter(Boolean).join(' ') || client.name || '?')
    : (displayNameProp || '?');
  const colorClass = getAvatarColor(colorSeed);

  return (
    <div className="relative flex-shrink-0">
      <div
        className={`${container} ${shapeClass} ${colorClass} flex items-center justify-center`}
        aria-label={colorSeed}
      >
        <span className={`text-white ${text} font-bold tracking-wide select-none`}>{initials}</span>
      </div>
      {hasUnread && (
        <span
          role="status"
          className="absolute -top-0.5 -right-0.5 block w-2.5 h-2.5 rounded-full bg-primary-500 border-2 border-white"
          title="Nouvelles réponses"
          aria-label="Nouvelles réponses"
        />
      )}
    </div>
  );
}
